import {useEffect, useMemo, useRef, useState} from "react";
import {useIntl} from "react-intl";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import {addWeeks, startOfWeek, addMonths, startOfMonth} from "date-fns";
import {notify} from "@housebets/housebets-x-package";

import {useAppSelector} from "hooks/useAppSelector";
import {setMonthRewardApprovedRate, setMonthRewardRate, setWeekRewardApprovedRate, setWeekRewardRate} from "app/store/slices/setting";
import apiUser from "app/api/user";
import apiAuth from "app/api/auth";
import {ISliderConfirmModalState} from "../types";
import {ContractOnceRequiresCallbackError} from "web3";
import {useDispatch} from "react-redux";

dayjs.extend(isoWeek);

export const useRewardSliderData = () => {
    const {$t} = useIntl();
    const dispatch = useDispatch();

    const token = useAppSelector((state) => state.user.token);
    const level = useAppSelector((state) => state.user.user?.level) || 0;
    const isLogin = useAppSelector((state) => state.user.isLogin);
    const enableVipChangeRewardRate = useAppSelector((state) => state.config.platformConfig?.enableVipChangeRewardRate);
    // values on hold to approve (in rewards dropdown)
    const weekRewardRate = useAppSelector((state) => state.setting.weekRewardRate);
    const monthRewardRate = useAppSelector((state) => state.setting.monthRewardRate);
    // actual values (in rewards dropdown)
    const weekRewardApprovedRate = useAppSelector((state) => state.setting.weekRewardApprovedRate);
    const monthRewardApprovedRate = useAppSelector((state) => state.setting.monthRewardApprovedRate);

    const vipList = useAppSelector((state) => state.config.platformConfig?.vipList || []);

    const [isOpen, setIsOpen] = useState(false);
    // const [weekEndTime, setWeekEndTime] = useState("");
    const weekEndTimeRef = useRef<number>(0);
    const monthEndTimeRef = useRef<number>(0);

    // const [monthEndTime, setMonthEndTime] = useState("");
    const [sliderTabIndex, setSliderTabIndex] = useState(0);
    const [weekValue, setWeekValue] = useState([50]);
    const [monthValue, setMonthValue] = useState([50]);
    const [initWeekValue, setInitWeekValue] = useState([0]);
    const [initMonthValue, setInitMonthValue] = useState([0]);
    const [isSliderConfirmModalOpen, setSliderConfirmModalOpen] = useState(false);
    const [sliderConfirmModal, setSliderConfirmModal] = useState<ISliderConfirmModalState>({
        isOpen: false,
        type: "week",
        timeLeftMonth: null,
        timeLeftWeek: null,
        processed: false,
    });
    const [disabledChangeInfoModal, setDisabledChangeInfoModal] = useState<ISliderConfirmModalState>({
        isOpen: false,
        type: "week",
        timeLeftMonth: null,
        timeLeftWeek: null,
        processed: false,
    });
    const [isFetching, setIsFetching] = useState(false);

    const updateStates = (weekRewardRate: number, monthRewardRate: number) => {
        let weekValue = [Math.round(weekRewardRate * 100)];
        let monthValue = [Math.round(monthRewardRate * 100)];

        if (weekRewardRate === 0 && monthRewardRate === 0) {
            weekValue = [50];
            monthValue = [50];
        }

        setWeekValue(weekValue);
        setMonthValue(monthValue);
        setInitWeekValue(weekValue);
        setInitMonthValue(monthValue);
    };

    const getVipMonthData = async (cb) => {
        const {status, data} = await apiAuth.getVipMonthData();
        if (status === 200 && data.code === 0) {
            const {weekTime, monthTime, userWeekRewardRate, userMonthRewardRate} = data.data;
            dispatch(setWeekRewardApprovedRate(userWeekRewardRate));
            dispatch(setMonthRewardApprovedRate(userMonthRewardRate));
            monthEndTimeRef.current = monthTime;
            weekEndTimeRef.current = weekTime;

            if (cb) {
                cb();
            }
        }
    };

    const onAfterSubmitShowTimerModal = async () => {
        const {status, data} = await apiUser.getUserOtherData(token || "");
        const {weekRewardRate, monthRewardRate, serverTime} = data.data;
        if (status === 200 && data.code === 0) {
            getVipMonthData(() => {
                showTimerModal(serverTime);
                dispatch(setWeekRewardRate(weekRewardRate));
                dispatch(setMonthRewardRate(monthRewardRate));
            });
        }
    };

    const getDates = (weekTime: number) => {
        const now = dayjs();
        const timeLeftWeek = now.add(+weekEndTimeRef.current, "millisecond");
        const timeLeftMonth = now.add(+monthEndTimeRef.current, "millisecond");

        const isDateIsFirstDayOnWeek = now.isoWeekday() === 1;
        const isDateIsInFirstThreeDaysOnMonth = now.date() <= 3;

        return {
            isDateIsFirstDayOnWeek,
            isDateIsInFirstThreeDaysOnMonth,
            weekGracePeriodEndStartDate: timeLeftWeek.toISOString(),
            monthGracePeriodEndStartDate: timeLeftMonth.toISOString(),
            timeLeftWeek: timeLeftWeek.toDate(),
            timeLeftMonth: timeLeftMonth.toDate(),
        };
    };

    const showTimerModal = async (serverTime: number) => {
        const {monthGracePeriodEndStartDate, weekGracePeriodEndStartDate} = getDates(serverTime);

        setSliderConfirmModal((prev) => ({
            ...prev,
            type: sliderTabIndex === 0 ? "week" : "month",
            processed: true,
            timeLeftMonth: new Date(monthGracePeriodEndStartDate),
            timeLeftWeek: new Date(weekGracePeriodEndStartDate),
        }));
    };

    const onConfirm = async () => {
        try {
            setIsFetching(true);
            const {status, data} = await apiUser.getUserOtherData(token || "");
            const {serverTime} = data.data;
            if (status === 200 && data.code === 0) {
                const {monthGracePeriodEndStartDate, weekGracePeriodEndStartDate} = getDates(serverTime);

                setSliderConfirmModal((prev) => ({
                    ...prev,
                    type: sliderTabIndex === 0 ? "week" : "month",
                    isOpen: true,
                    processed: false,
                    timeLeftMonth: new Date(monthGracePeriodEndStartDate),
                    timeLeftWeek: new Date(weekGracePeriodEndStartDate),
                }));
            }
        } finally {
            setIsFetching(false);
        }
    };

    const onSubmit = async () => {
        if (level < 7) return;

        try {
            setIsFetching(true);
            const payLoad = {
                weekRewardRate: weekValue[0] / 100,
                monthRewardRate: monthValue[0] / 100,
            };
            const res = await apiUser.changeRewardRate(payLoad);
            if (res.status === 200 && res.data.code === 0) {
                onAfterSubmitShowTimerModal();
                notify({
                    type: "success",
                    title: $t({id: "UpdatedSuccess"}),
                });
                return;
            }
            notify({
                type: "error",
                title: `[code: ${res.data.code}]`,
            });
        } finally {
            setIsFetching(false);
        }
    };

    const rewardRateMax = useMemo(() => {
        const currentVtipLevelData = vipList.find((v: any) => v.level === level) || {weekRewardRateMax: 0, monthRewardRateMax: 0};

        const res = {
            weekMax: Math.round(currentVtipLevelData.weekRewardRateMax * 100),
            monthMax: Math.round(currentVtipLevelData.monthRewardRateMax * 100),
        };

        return res;
    }, [vipList, level]);

    // slider week config
    const weekRange = useMemo(() => {
        return {
            min: 0,
            max: 100,
            values: weekValue,
            minAvailable: 50 - rewardRateMax.weekMax,
            maxAvailable: 50 + rewardRateMax.weekMax,
            step: 5,
        };
    }, [weekValue, rewardRateMax]);

    // slider month config
    const monthRange = useMemo(() => {
        return {
            min: 0,
            max: 100,
            values: monthValue,
            minAvailable: 50 - rewardRateMax.monthMax,
            maxAvailable: 50 + rewardRateMax.monthMax,
            step: 5,
        };
    }, [monthValue, rewardRateMax]);

    // dropdown week config
    const weekDropdownValue = useMemo(() => {
        const value = weekRewardApprovedRate === 0 && monthRewardApprovedRate === 0 ? 0.5 : weekRewardApprovedRate;

        return {
            lossback: level < 7 ? 50 : 100 - value * 100,
            rakeback: level < 7 ? 50 : value * 100,
            isLocked: level < 7,
        };
    }, [weekRewardApprovedRate, monthRewardApprovedRate, level]);

    // dropdown month config
    const monthDropdownValue = useMemo(() => {
        const value = weekRewardApprovedRate === 0 && monthRewardApprovedRate === 0 ? 0.5 : monthRewardApprovedRate;

        return {
            lossback: level < 16 ? 50 : 100 - value * 100,
            rakeback: level < 16 ? 50 : value * 100,
            isLocked: level < 16,
        };
    }, [monthRewardApprovedRate, weekRewardApprovedRate, level]);

    const showConfirmButton = useMemo(() => {
        if (sliderTabIndex === 0) {
            return initWeekValue[0] !== weekValue[0];
        }

        return initMonthValue[0] !== monthValue[0];
    }, [sliderTabIndex, initWeekValue, weekValue, initMonthValue, monthValue]);

    useEffect(() => {
        const type = sliderTabIndex === 0 ? "week" : "month";

        setSliderConfirmModal((prev) => ({
            ...prev,
            type,
        }));
        setDisabledChangeInfoModal((prev) => ({
            ...prev,
            type,
        }));
    }, [sliderTabIndex]);

    useEffect(() => {
        updateStates(weekRewardRate, monthRewardRate);
    }, [weekRewardRate, monthRewardRate]);

    return {
        weekRange,
        weekValue,
        setWeekValue,
        monthRange,
        monthValue,
        weekDropdownValue,
        monthDropdownValue,
        setMonthValue,
        onConfirm,
        onSubmit,
        isOpen,
        setIsOpen,
        isSliderConfirmModalOpen,
        setSliderConfirmModalOpen,
        isLogin,
        enableVipChangeRewardRate,
        level,
        sliderTabIndex,
        setSliderTabIndex,
        showConfirmButton,
        isFetching,
        sliderConfirmModal,
        setSliderConfirmModal,
        disabledChangeInfoModal,
        setDisabledChangeInfoModal,
    };
};
